import React from "react";
import { Link } from "react-router-dom";
import DiscoveryShelf from "./DiscoveryShelf";
import ReaderHeader from "./ReaderHeader";

const chapterTwoCards = [
  {
    title: "Find a story for your mood",
    text: "Wander the marketplace by genre and meet independent voices you might not have found yet.",
  },
  {
    title: "Meet the author behind it",
    text: "Visit an author’s own corner of the Circle to explore their books, read their updates, and learn about their writing world.",
  },
  {
    title: "Keep a place on your shelf",
    text: "Save books to your to-be-read list (TBR), ready for the next time you wonder what to read.",
  },
];

const chapterFourCards = [
  {
    title: "Follow your favorite authors",
    text: "Found a voice you love? Follow the author to find their updates in your Circle activity. No purchase required.",
  },
  {
    title: "Explore your genre circles",
    text: "Follow genres that match your reading mood and find book updates from those circles in your Reader Portal.",
  },
  {
    title: "Return when you’re ready",
    text: "Your saved books and followed circles give you a familiar place to pick up your next chapter.",
  },
  {
    title: "A thank-you from participating authors",
    text: "When you choose to shop, sign in to receive eligible Reader Appreciation discounts in participating authors’ Bookish Circle stores.",
  },
];

const steps = [
  {
    step: "Step 1",
    title: "Create your free reader account",
    text: "Choose your favorite genres, verify your email, and make yourself at home in your Reader Portal.",
  },
  {
    step: "Step 2",
    title: "Explore and save",
    text: "Browse the books, visit author shops, and save the stories that catch your eye to your TBR list.",
  },
  {
    step: "Step 3",
    title: "Keep in touch",
    text: "Follow authors and genres, then check your Circle activity for their book updates.",
  },
];

const quickHighlights = [
  "Discover independent authors and your next favorite book",
  "Save your next reads and follow authors and genres",
  "Enjoy eligible discounts from participating authors",
];

const readerQuestions = [
  {
    question: "Is a Circle Reader account free?",
    answer: "Yes. Creating your reader account is free, with no reader membership subscription fee. Books and merchandise are purchased separately when you choose to shop.",
  },
  {
    question: "Can I browse before creating an account?",
    answer: "Of course. Explore the marketplace and author shops without signing up. Create a free account when you want to save books, follow authors and genres, or receive eligible Reader Appreciation discounts.",
  },
  {
    question: "Do I need to buy a book to follow an author?",
    answer: "No purchase is required. Sign in to your reader account and follow the authors or genres you’re interested in.",
  },
  {
    question: "Which purchases qualify for Reader Appreciation?",
    answer: "Sign in and buy directly from a participating author’s Bookish Circle shop to receive $1 off each ebook priced $3 or more and 10% off physical books and bookish merchandise. Ebooks below $3 keep their usual price. Shipping, guest checkout, and purchases at external retailers do not receive these discounts. Authors choose whether to participate.",
  },
];

const chapterCardClass =
  "reader-chapter rounded-2xl border border-bookish-purple/20 bg-[#FBF8F3] px-6 sm:px-8 py-8 sm:py-10 mb-8";

const ReaderLanding = () => {
  return (
    <div className="public-design reader-story min-h-screen">
      {/* HEADER */}
      <ReaderHeader />

      {/* MAIN */}
      <main className="public-container pt-10 md:pt-14 pb-8 md:pb-14">


        {/* CONTENT */}
        <div>
          {/* HERO */}
          <section className="public-hero reader-hero">
            <div>
              <p className="uppercase tracking-[0.2em] text-xs text-bookish-purpleAccent mb-3">
                Your reader story begins here
              </p>
              <h1 className="text-3xl sm:text-4xl font-semibold text-bookish-purple mb-4">
                Join your next favorite reading community.
              </h1>
              <p className="text-bookish-purpleAccent max-w-2xl mb-6">
                Bookish Circle helps readers discover independent authors,
                save their next reads, and stay connected to the stories they love — all
                in one place.
              </p>
              <ul className="space-y-2 text-bookish-purpleAccent">
                {quickHighlights.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span aria-hidden="true" className="mt-2 h-2 w-2 shrink-0 rounded-full bg-bookish-royal" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="public-actions mt-6"><Link className="public-button" to="/signupreader">Create your free reader account →</Link><Link className="public-button-secondary" to="/market">Browse books</Link></div>
              <p className="public-note">Browse freely — no account needed. Already part of the Circle? <Link to="/loginreader">Log in</Link></p>
            </div>
            <DiscoveryShelf />
          </section>

          <section className={chapterCardClass} aria-labelledby="reader-perks-heading">
            <p className="uppercase text-xs tracking-[0.25em] text-bookish-purpleAccent mb-2">
              Reader Appreciation
            </p>
            <h2 id="reader-perks-heading" className="text-2xl sm:text-3xl font-semibold text-bookish-purple mb-4">
              Free to join. More to enjoy.
            </h2>
            <p className="text-bookish-purpleAccent mb-4">
              Sign up for a free Circle Reader membership and enjoy these perks from participating authors:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-bookish-purpleAccent">
              <li><strong>10% off physical books and bookish merchandise.</strong></li>
              <li><strong>$1 off ebooks priced $3 or more.</strong></li>
            </ul>
            <p className="text-sm text-bookish-purpleAccent mt-4">
              Just sign in when you shop. Your discounts apply automatically at checkout in participating authors’ Bookish Circle shops. Shipping is not discounted.
            </p>
            <div className="public-actions mt-5">
              <Link className="public-button" to="/signupreader">Create your free reader account →</Link>
            </div>
          </section>

          {/* CHAPTER ONE */}
          <section className={chapterCardClass}>
            <p className="uppercase text-xs tracking-[0.25em] text-bookish-purpleAccent mb-2">
              Chapter One
            </p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-bookish-purple mb-4">
              So many stories. Where did you save that one?
            </h2>
            <ul className="list-disc pl-5 space-y-2 text-bookish-purpleAccent">
              <li>A recommendation you meant to write down.</li>
              <li>An author whose next book you wanted to look out for.</li>
              <li>A reading list scattered across bookmarks and notes.</li>
            </ul>
            <p className="text-bookish-purpleAccent mt-4">Bring those little bookish discoveries together, so the next story is easier to find when you’re ready for it.</p>
          </section>

          {/* CHAPTER TWO */}
          <section className={chapterCardClass}>
            <p className="uppercase text-xs tracking-[0.25em] text-bookish-purpleAccent mb-2">
              Chapter Two
            </p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-bookish-purple mb-6">
              Find your next “just one more chapter”
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {chapterTwoCards.map((card) => (
                <article
                  key={card.title}
                  className="bg-white/80 border border-[#d8cdb5] rounded-xl p-5"
                >
                  <h3 className="text-lg font-semibold text-bookish-purple mb-2">
                    {card.title}
                  </h3>
                  <p className="text-bookish-purpleAccent text-sm">
                    {card.text}
                  </p>
                </article>
              ))}
            </div>
          </section>

          {/* CHAPTER THREE – TRUE CIRCLES, LEFT ALIGNED */}
          <section className={chapterCardClass}>
            <p className="uppercase text-xs tracking-[0.25em] text-bookish-purpleAccent mb-2">
              Chapter Three
            </p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-bookish-purple mb-6">
              How It Works
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {steps.map((entry) => (
                <article
                  key={entry.title}
                  className="rounded-2xl border border-bookish-royal/30 bg-white/80 p-6 flex flex-col items-center"
                >
                  <span className="mb-3 text-xs border border-bookish-purple rounded-full w-14 h-14 flex items-center justify-center text-bookish-purpleAccent">
                    {entry.step}
                  </span>
                  <h3 className="text-lg font-semibold text-bookish-purple mb-2 text-center">
                    {entry.title}
                  </h3>
                  <p className="text-sm text-bookish-purpleAccent text-center">
                    {entry.text}
                  </p>
                </article>
              ))}
            </div>
          </section>

          {/* CHAPTER FOUR */}
          <section className={chapterCardClass}>
            <p className="uppercase text-xs tracking-[0.25em] text-bookish-purpleAccent mb-2">
              Chapter Four
            </p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-bookish-purple mb-6">
              Stay close to the stories you love
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {chapterFourCards.map((card) => (
                <article
                  key={card.title}
                  className="bg-white/80 border border-[#d8cdb5] rounded-xl p-5"
                >
                  <h3 className="text-lg font-semibold text-bookish-purple mb-2">
                    {card.title}
                  </h3>
                  <p className="text-bookish-purpleAccent text-sm">
                    {card.text}
                  </p>
                </article>
              ))}
            </div>
          </section>

          <section className={chapterCardClass} aria-labelledby="reader-faq-heading">
            <p className="uppercase text-xs tracking-[0.25em] text-bookish-purpleAccent mb-2">Chapter Five</p>
            <h2 id="reader-faq-heading" className="text-2xl sm:text-3xl font-semibold text-bookish-purple mb-6">A few questions before your next chapter</h2>
            {readerQuestions.map(({ question, answer }) => (
              <details key={question} className="border-t border-bookish-purple/20 py-4">
                <summary className="cursor-pointer py-2 font-semibold text-bookish-purple">{question}</summary>
                <p className="mt-3 text-bookish-purpleAccent leading-relaxed">{answer}</p>
              </details>
            ))}
          </section>

          {/* CTA */}
          <div className="max-w-5xl mx-auto mt-16 pt-10 border-t border-[#dccfb8] text-center">
            <h2 className="text-4xl sm:text-5xl font-semibold text-bookish-purple mb-6">
              Ready to find your place?
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signupreader"
                className="public-button"
              >
                Create your free reader account →
              </Link>
              <Link
                to="/loginreader"
                className="public-button-secondary"
              >
                Log in to your reader account
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReaderLanding;
